import { useEffect, useMemo, useState } from "react";
import { useAuth } from "../../context/AuthContext";
import {
  createPlatformTenant,
  listPlatformPlans,
  listPlatformTenants,
  recordTenantPayment,
  suspendTenant,
  updatePlatformAccount,
  updateTenantIndustry,
  updateTenantModule,
  updateTenantPlan,
} from "../../api/platform";
import { TENANT_INDUSTRY } from "../../utils/industryLabels";
import "./CompanyModulesPanel.css";

export const COMPANY_MODULES = [
  { key: "leave", label: "الإجازات والأذونات" },
  { key: "attendance", label: "الحضور والانصراف" },
  { key: "overtime", label: "العمل الإضافي" },
  { key: "payroll", label: "المرتبات والتارجت" },
  { key: "complaints", label: "الشكاوى والتحقيقات" },
  { key: "evaluations", label: "تقييم الأداء" },
  { key: "recruitment", label: "التوظيف" },
];

const BADGE_LABELS = {
  trial: "تجريبي",
  active: "ساري",
  grace: "في السماح",
  suspended: "موقوف",
};

function isPlatformAdmin(user) {
  return (
    user?.role === "platform-admin" ||
    user?.backendRoles?.includes("platform_admin")
  );
}

function accountErrorMessage(error) {
  const raw = error?.response?.data?.message;
  if (Array.isArray(raw)) return raw.join(" — ");
  if (typeof raw === "string" && raw.trim()) return raw;
  return "تعذر حفظ بيانات الدخول. حاول مرة أخرى.";
}

function apiErrorMessage(error, fallback) {
  const raw = error?.response?.data?.message;
  if (Array.isArray(raw)) return raw.join(" — ");
  if (typeof raw === "string" && raw.trim()) return raw;
  return fallback;
}

const EMPTY_CREATE_FORM = {
  name: "",
  slug: "",
  industry: TENANT_INDUSTRY.COMPANY,
  planId: "",
  adminEmail: "",
  adminUsername: "",
  adminPassword: "",
};

const INDUSTRY_OPTIONS = [
  { value: TENANT_INDUSTRY.COMPANY, label: "شركة (فروع / مقر)" },
  { value: TENANT_INDUSTRY.FACTORY, label: "مصنع (مواقع إنتاج)" },
];

function CreateCompanyForm({ plans, onCreated }) {
  const [form, setForm] = useState(EMPTY_CREATE_FORM);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [saved, setSaved] = useState("");

  function setField(field, value) {
    setForm((prev) => ({ ...prev, [field]: value }));
  }

  async function handleSubmit(event) {
    event.preventDefault();
    setError("");
    setSaved("");

    const name = form.name.trim();
    const slug = form.slug.trim().toLowerCase();
    const planId = form.planId.trim();
    const adminEmail = form.adminEmail.trim().toLowerCase();
    const adminUsername = form.adminUsername.trim().toLowerCase();
    const adminPassword = form.adminPassword.trim();

    if (!name || !slug || !planId || !adminEmail || !adminUsername || !adminPassword) {
      setError("أكمل كل الحقول المطلوبة");
      return;
    }
    if (!/^[a-z0-9][a-z0-9-]{1,48}[a-z0-9]$/.test(slug)) {
      setError("كود الدخول: حروف إنجليزية صغيرة وأرقام وشرطات فقط");
      return;
    }
    if (adminPassword.length < 8) {
      setError("كلمة مرور المدير العام 8 أحرف على الأقل");
      return;
    }

    setSaving(true);
    try {
      const created = await createPlatformTenant({
        name,
        slug,
        industry: form.industry || TENANT_INDUSTRY.COMPANY,
        planId,
        adminEmail,
        adminUsername,
        adminPassword,
      });
      setForm({
        ...EMPTY_CREATE_FORM,
        planId: plans[0]?.id || "",
      });
      const planName = plans.find((plan) => plan.id === planId)?.name || "";
      const kindLabel =
        (created?.industry || form.industry) === TENANT_INDUSTRY.FACTORY
          ? "مصنع"
          : "شركة";
      setSaved(
        `تم إنشاء ${kindLabel} «${created?.name || name}» بكود ${
          created?.slug || slug
        }${planName ? ` — باقة ${planName}` : ""}. تجربة 14 يوم.`,
      );
      onCreated?.(created);
    } catch (err) {
      setError(
        apiErrorMessage(err, "تعذر إنشاء الشركة أو المصنع. حاول مرة أخرى."),
      );
    } finally {
      setSaving(false);
    }
  }

  useEffect(() => {
    if (!form.planId && plans[0]?.id) {
      setForm((prev) => ({ ...prev, planId: plans[0].id }));
    }
  }, [plans, form.planId]);

  const selectedPlan = plans.find((plan) => plan.id === form.planId);

  return (
    <form className="company-modules-create" onSubmit={handleSubmit}>
      <h2 className="company-modules-name">إضافة شركة أو مصنع</h2>
      <p className="company-modules-subtitle">
        اختر النوع والباقة لتحديد حد الموظفين والوحدات، ثم أنشئ حساب المدير
        العام. الاشتراك يبدأ بتجربة 14 يومًا. المصنع يفعّل هيكل مواقع الإنتاج
        تلقائيًا.
      </p>

      <div className="company-modules-create-grid">
        <label className="company-modules-label">
          النوع
          <select
            className="company-modules-select"
            value={form.industry}
            onChange={(event) => setField("industry", event.target.value)}
            aria-label="نوع المنشأة"
          >
            {INDUSTRY_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </label>
        <label className="company-modules-label">
          الاسم
          <input
            className="company-modules-input"
            value={form.name}
            onChange={(event) => setField("name", event.target.value)}
            placeholder={
              form.industry === TENANT_INDUSTRY.FACTORY
                ? "مصنع أكمي"
                : "شركة أكمي"
            }
            autoComplete="organization"
          />
        </label>
        <label className="company-modules-label">
          كود الدخول
          <input
            className="company-modules-input"
            value={form.slug}
            onChange={(event) => setField("slug", event.target.value)}
            placeholder="acme"
            autoCapitalize="none"
            spellCheck={false}
          />
        </label>
        <label className="company-modules-label">
          الباقة / حد الموظفين
          <select
            className="company-modules-select"
            value={form.planId}
            onChange={(event) => setField("planId", event.target.value)}
            aria-label="باقة الشركة الجديدة"
          >
            {plans.length === 0 ? (
              <option value="">لا توجد باقات</option>
            ) : (
              plans.map((plan) => (
                <option key={plan.id} value={plan.id}>
                  {plan.name} — حتى {plan.employeeLimit} موظف
                  {plan.monthlyPrice != null
                    ? ` (${Number(plan.monthlyPrice)} ج.م / شهر)`
                    : ""}
                </option>
              ))
            )}
          </select>
        </label>
        {selectedPlan ? (
          <div className="company-modules-create-hint">
            الحد: <strong>{selectedPlan.employeeLimit}</strong> موظف غير منتهي
            الخدمة
          </div>
        ) : null}
        <label className="company-modules-label">
          بريد المدير العام
          <input
            className="company-modules-input"
            type="email"
            value={form.adminEmail}
            onChange={(event) => setField("adminEmail", event.target.value)}
            placeholder="gm@acme.com"
            autoComplete="off"
          />
        </label>
        <label className="company-modules-label">
          اسم مستخدم المدير العام
          <input
            className="company-modules-input"
            value={form.adminUsername}
            onChange={(event) => setField("adminUsername", event.target.value)}
            placeholder="gm"
            autoComplete="off"
          />
        </label>
        <label className="company-modules-label">
          كلمة مرور المدير العام
          <input
            className="company-modules-input"
            type="password"
            value={form.adminPassword}
            onChange={(event) => setField("adminPassword", event.target.value)}
            placeholder="8 أحرف على الأقل"
            autoComplete="new-password"
          />
        </label>
      </div>

      {error ? (
        <div className="company-modules-banner is-error" role="alert">
          {error}
        </div>
      ) : null}
      {saved ? (
        <div className="company-modules-banner" role="status">
          {saved}
        </div>
      ) : null}

      <button
        type="submit"
        className="company-modules-save"
        disabled={saving || plans.length === 0}
      >
        {saving
          ? "جارٍ الإنشاء..."
          : form.industry === TENANT_INDUSTRY.FACTORY
            ? "إنشاء المصنع"
            : "إنشاء الشركة"}
      </button>
    </form>
  );
}

function formatDate(value) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleDateString("ar-EG");
}

function mergeCompany(existing, updated) {
  if (!updated) return existing;
  return {
    ...existing,
    ...updated,
    modules: updated.modules || existing.modules,
    plan: updated.plan || existing.plan,
  };
}

function PlatformAccountForm({ user }) {
  const { patchUser } = useAuth();
  const currentUsername = user?.username || user?.name || "";
  const [username, setUsername] = useState(currentUsername);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [saved, setSaved] = useState(false);

  async function handleSubmit(event) {
    event.preventDefault();
    setError("");
    setSaved(false);

    const nextUsername = username.trim().toLowerCase();
    const nextPassword = newPassword.trim();
    const usernameChanged = nextUsername && nextUsername !== currentUsername;
    if (!usernameChanged && !nextPassword) {
      setError("غيّر اسم المستخدم أو اكتب كلمة مرور جديدة");
      return;
    }
    if (!currentPassword.trim()) {
      setError("كلمة المرور الحالية مطلوبة");
      return;
    }
    if (nextPassword && nextPassword !== confirmPassword.trim()) {
      setError("تأكيد كلمة المرور غير مطابق");
      return;
    }
    if (nextPassword && nextPassword === currentPassword.trim()) {
      setError("كلمة المرور الجديدة يجب أن تكون مختلفة عن الحالية");
      return;
    }

    const payload = { currentPassword: currentPassword.trim() };
    if (usernameChanged) payload.username = nextUsername;
    if (nextPassword) {
      payload.newPassword = nextPassword;
      payload.confirmPassword = confirmPassword.trim();
    }

    setSaving(true);
    try {
      const updated = await updatePlatformAccount(payload);
      const savedUsername = updated?.username || nextUsername || currentUsername;
      patchUser?.({
        username: savedUsername,
        name: savedUsername,
        code: String(savedUsername).toUpperCase(),
      });
      setUsername(savedUsername);
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      setSaved(true);
    } catch (err) {
      setError(accountErrorMessage(err));
    } finally {
      setSaving(false);
    }
  }

  return (
    <form className="company-modules-account" onSubmit={handleSubmit}>
      <h2 className="company-modules-name">حساب سوبر أدمن المنصة</h2>
      <p className="company-modules-subtitle">
        غيّر اسم المستخدم أو كلمة المرور لحساب المنصة فقط.
      </p>

      <label className="company-modules-label" htmlFor="platform-username">
        اسم المستخدم
      </label>
      <input
        id="platform-username"
        className="company-modules-input"
        value={username}
        onChange={(event) => setUsername(event.target.value)}
        autoComplete="username"
      />

      <label className="company-modules-label" htmlFor="platform-current-password">
        كلمة المرور الحالية
      </label>
      <input
        id="platform-current-password"
        type="password"
        className="company-modules-input"
        value={currentPassword}
        onChange={(event) => setCurrentPassword(event.target.value)}
        autoComplete="current-password"
      />

      <label className="company-modules-label" htmlFor="platform-new-password">
        كلمة المرور الجديدة
      </label>
      <input
        id="platform-new-password"
        type="password"
        className="company-modules-input"
        value={newPassword}
        onChange={(event) => setNewPassword(event.target.value)}
        autoComplete="new-password"
      />

      <label
        className="company-modules-label"
        htmlFor="platform-confirm-password"
      >
        تأكيد كلمة المرور
      </label>
      <input
        id="platform-confirm-password"
        type="password"
        className="company-modules-input"
        value={confirmPassword}
        onChange={(event) => setConfirmPassword(event.target.value)}
        autoComplete="new-password"
      />

      {error ? (
        <div className="company-modules-banner is-error" role="alert">
          {error}
        </div>
      ) : null}
      {saved ? (
        <div className="company-modules-banner" role="status">
          تم حفظ بيانات الدخول
        </div>
      ) : null}

      <button
        type="submit"
        className="company-modules-save"
        disabled={saving}
      >
        {saving ? "جارٍ الحفظ..." : "حفظ بيانات الدخول"}
      </button>
    </form>
  );
}

function CompanyModulesPanel() {
  const { user } = useAuth();
  const allowed = isPlatformAdmin(user);
  const [companies, setCompanies] = useState([]);
  const [plans, setPlans] = useState([]);
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [pendingKey, setPendingKey] = useState("");
  const [paymentDrafts, setPaymentDrafts] = useState({});

  useEffect(() => {
    if (!allowed) return undefined;
    let cancelled = false;

    Promise.all([listPlatformTenants(), listPlatformPlans()])
      .then(([rows, planRows]) => {
        if (cancelled) return;
        setCompanies(Array.isArray(rows) ? rows : []);
        setPlans(Array.isArray(planRows) ? planRows : []);
      })
      .catch(() => {
        if (!cancelled) setError("تعذر تحميل الشركات");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [allowed]);

  const visible = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return companies;
    return companies.filter((company) => {
      const name = String(company.name || "").toLowerCase();
      const slug = String(company.slug || "").toLowerCase();
      return name.includes(needle) || slug.includes(needle);
    });
  }, [companies, query]);

  function patchCompany(updated) {
    if (!updated?.id) return;
    setCompanies((rows) =>
      rows.map((row) =>
        row.id === updated.id ? mergeCompany(row, updated) : row,
      ),
    );
  }

  function getPaymentDraft(companyId) {
    return (
      paymentDrafts[companyId] || {
        amount: "",
        months: "1",
        note: "",
      }
    );
  }

  function setPaymentField(companyId, field, value) {
    setPaymentDrafts((prev) => ({
      ...prev,
      [companyId]: {
        ...getPaymentDraft(companyId),
        ...prev[companyId],
        [field]: value,
      },
    }));
  }

  async function toggleModule(company, moduleKey) {
    const current = company.modules?.[moduleKey] !== false;
    const next = !current;
    const token = `${company.id}:${moduleKey}`;
    setPendingKey(token);
    setError("");
    setCompanies((rows) =>
      rows.map((row) =>
        row.id === company.id
          ? { ...row, modules: { ...row.modules, [moduleKey]: next } }
          : row,
      ),
    );

    try {
      const updated = await updateTenantModule(company.id, moduleKey, next);
      if (updated?.modules) {
        setCompanies((rows) =>
          rows.map((row) =>
            row.id === company.id ? { ...row, modules: updated.modules } : row,
          ),
        );
      }
    } catch {
      setCompanies((rows) =>
        rows.map((row) =>
          row.id === company.id
            ? { ...row, modules: { ...row.modules, [moduleKey]: current } }
            : row,
        ),
      );
      setError("تعذر حفظ حالة الموديول. حاول مرة أخرى.");
    } finally {
      setPendingKey("");
    }
  }

  async function changeIndustry(company, industry) {
    if (!industry || industry === company.industry) return;
    const token = `${company.id}:industry`;
    setPendingKey(token);
    setError("");
    try {
      const updated = await updateTenantIndustry(company.id, industry);
      patchCompany(updated);
    } catch {
      setError("تعذر تحديث نوع المنشأة. حاول مرة أخرى.");
    } finally {
      setPendingKey("");
    }
  }

  async function changePlan(company, planId) {
    if (!planId || planId === company.planId) return;
    const token = `${company.id}:plan`;
    setPendingKey(token);
    setError("");
    try {
      const updated = await updateTenantPlan(company.id, planId);
      patchCompany(updated);
    } catch {
      setError("تعذر تغيير الباقة. حاول مرة أخرى.");
    } finally {
      setPendingKey("");
    }
  }

  async function submitPayment(company) {
    const draft = getPaymentDraft(company.id);
    const amount = Number(draft.amount);
    const months = Number.parseInt(draft.months, 10);
    if (!Number.isFinite(amount) || amount < 0) {
      setError("أدخل مبلغ الدفعة بشكل صحيح");
      return;
    }
    if (!Number.isFinite(months) || months < 1) {
      setError("عدد الأشهر يجب أن يكون 1 على الأقل");
      return;
    }

    const token = `${company.id}:payment`;
    setPendingKey(token);
    setError("");
    try {
      const updated = await recordTenantPayment(company.id, {
        amount,
        months,
        note: draft.note?.trim() || undefined,
      });
      patchCompany(updated);
      setPaymentDrafts((prev) => ({
        ...prev,
        [company.id]: { amount: "", months: "1", note: "" },
      }));
    } catch {
      setError("تعذر تسجيل الدفعة. حاول مرة أخرى.");
    } finally {
      setPendingKey("");
    }
  }

  async function handleSuspend(company) {
    if (company.status === "SUSPENDED") return;
    const token = `${company.id}:suspend`;
    setPendingKey(token);
    setError("");
    try {
      const updated = await suspendTenant(company.id);
      patchCompany(updated);
    } catch {
      setError("تعذر إيقاف الشركة. حاول مرة أخرى.");
    } finally {
      setPendingKey("");
    }
  }

  if (!allowed) {
    return (
      <div className="company-modules" dir="rtl">
        <div className="company-modules-banner is-error">
          هذه اللوحة متاحة لسوبر أدمن المنصة فقط
        </div>
      </div>
    );
  }

  function handleCompanyCreated(created) {
    if (!created?.id) return;
    setCompanies((rows) => {
      if (rows.some((row) => row.id === created.id)) {
        return rows.map((row) =>
          row.id === created.id ? mergeCompany(row, created) : row,
        );
      }
      return [created, ...rows];
    });
  }

  return (
    <div className="company-modules" dir="rtl">
      <PlatformAccountForm user={user} />

      <header className="company-modules-header">
        <h1 className="company-modules-title">
          اشتراكات وموديولات الشركات والمصانع
        </h1>
        <p className="company-modules-subtitle">
          أنشئ شركة أو مصنع بباقة وحد موظفين، تابع الاشتراك، سجّل الدفعات،
          وأوقف أو افتح الموديولات.
        </p>
      </header>

      <CreateCompanyForm plans={plans} onCreated={handleCompanyCreated} />

      <input
        className="company-modules-search"
        value={query}
        onChange={(event) => setQuery(event.target.value)}
        placeholder="ابحث بالاسم أو الكود"
        aria-label="بحث عن شركة أو مصنع"
      />

      {error ? (
        <div className="company-modules-banner is-error" role="alert">
          {error}
        </div>
      ) : null}

      {loading ? (
        <div className="company-modules-banner">جارٍ تحميل الشركات...</div>
      ) : null}

      {!loading && visible.length === 0 ? (
        <div className="company-modules-empty">لا توجد شركات مطابقة</div>
      ) : null}

      <div className="company-modules-list">
        {visible.map((company) => {
          const badge = company.subscriptionBadge || "active";
          const draft = getPaymentDraft(company.id);
          return (
            <article key={company.id} className="company-modules-card">
              <div className="company-modules-card-head">
                <div>
                  <h2 className="company-modules-name">{company.name}</h2>
                  <div className="company-modules-slug">{company.slug}</div>
                </div>
                <span
                  className={`company-modules-status${
                    badge === "suspended" || company.status !== "ACTIVE"
                      ? " is-off"
                      : badge === "grace"
                        ? " is-grace"
                        : badge === "trial"
                          ? " is-trial"
                          : ""
                  }`}
                >
                  {BADGE_LABELS[badge] ||
                    (company.status === "ACTIVE" ? "نشطة" : "موقوفة")}
                </span>
              </div>

              <div className="company-modules-meta">
                <div>
                  <span className="company-modules-meta-label">النوع</span>
                  <select
                    className="company-modules-select"
                    value={company.industry || TENANT_INDUSTRY.COMPANY}
                    disabled={pendingKey === `${company.id}:industry`}
                    onChange={(event) =>
                      changeIndustry(company, event.target.value)
                    }
                    aria-label={`نوع ${company.name}`}
                  >
                    {INDUSTRY_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <span className="company-modules-meta-label">الباقة</span>
                  <select
                    className="company-modules-select"
                    value={company.planId || ""}
                    disabled={pendingKey === `${company.id}:plan`}
                    onChange={(event) =>
                      changePlan(company, event.target.value)
                    }
                    aria-label={`باقة ${company.name}`}
                  >
                    <option value="" disabled>
                      اختر باقة
                    </option>
                    {plans.map((plan) => (
                      <option key={plan.id} value={plan.id}>
                        {plan.name} — حتى {plan.employeeLimit} موظف
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <span className="company-modules-meta-label">
                    نهاية الفترة
                  </span>
                  <strong>{formatDate(company.periodEnd)}</strong>
                </div>
                <div>
                  <span className="company-modules-meta-label">حد الموظفين</span>
                  <strong>{company.plan?.employeeLimit ?? "—"}</strong>
                </div>
              </div>

              <div className="company-modules-payment">
                <h3 className="company-modules-section-title">تسجيل دفعة</h3>
                <div className="company-modules-payment-grid">
                  <label className="company-modules-label">
                    المبلغ
                    <input
                      className="company-modules-input"
                      type="number"
                      min="0"
                      step="0.01"
                      value={draft.amount}
                      onChange={(event) =>
                        setPaymentField(company.id, "amount", event.target.value)
                      }
                    />
                  </label>
                  <label className="company-modules-label">
                    عدد الأشهر
                    <input
                      className="company-modules-input"
                      type="number"
                      min="1"
                      max="36"
                      value={draft.months}
                      onChange={(event) =>
                        setPaymentField(company.id, "months", event.target.value)
                      }
                    />
                  </label>
                  <label className="company-modules-label">
                    ملاحظة
                    <input
                      className="company-modules-input"
                      value={draft.note}
                      onChange={(event) =>
                        setPaymentField(company.id, "note", event.target.value)
                      }
                      placeholder="تحويل بنكي / إنستاباي"
                    />
                  </label>
                </div>
                <div className="company-modules-actions">
                  <button
                    type="button"
                    className="company-modules-save"
                    disabled={pendingKey === `${company.id}:payment`}
                    onClick={() => submitPayment(company)}
                  >
                    {pendingKey === `${company.id}:payment`
                      ? "جارٍ التسجيل..."
                      : "تسجيل الدفعة وتمديد الاشتراك"}
                  </button>
                  {company.status === "ACTIVE" ? (
                    <button
                      type="button"
                      className="company-modules-danger"
                      disabled={pendingKey === `${company.id}:suspend`}
                      onClick={() => handleSuspend(company)}
                    >
                      {pendingKey === `${company.id}:suspend`
                        ? "جارٍ الإيقاف..."
                        : "إيقاف يدوي"}
                    </button>
                  ) : null}
                </div>
              </div>

              <h3 className="company-modules-section-title">الموديولات</h3>
              <div className="company-modules-grid">
                {COMPANY_MODULES.map((module) => {
                  const open = company.modules?.[module.key] !== false;
                  const token = `${company.id}:${module.key}`;
                  return (
                    <button
                      key={module.key}
                      type="button"
                      className={`company-modules-switch${open ? "" : " is-off"}`}
                      aria-pressed={open}
                      disabled={pendingKey === token}
                      onClick={() => toggleModule(company, module.key)}
                    >
                      <span className="company-modules-switch-label">
                        {module.label}
                      </span>
                      <span className="company-modules-switch-state">
                        {open ? "مفتوح" : "مقفول"}
                      </span>
                    </button>
                  );
                })}
              </div>
            </article>
          );
        })}
      </div>
    </div>
  );
}

export default CompanyModulesPanel;
